/**
 * Solar System Animation Component
 *
 * Displays an animated solar system with the Gravity logo at the center
 * and token logos orbiting at different periods, inspired by the polar clock visualization.
 *
 * Token data is fetched dynamically from the backend, which randomly selects
 * tokens with icons from the supported tokens list for variety.
 */

import { useEffect, useRef, useState } from 'react';
import { BACKEND_API_URL } from '../../config/endpoints';

// Simple seeded random number generator for consistent star positions
function seededRandom(seed: number): () => number {
  let state = seed;
  return (): number => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

// Pre-generate star data with seeded random
interface StarData {
  width: number;
  height: number;
  left: string;
  top: string;
  opacity: number;
  animationDelay: string;
  animationDuration: string;
}

function generateStars(count: number): StarData[] {
  const random = seededRandom(42); // Use fixed seed for consistent rendering
  return Array.from({ length: count }).map(() => {
    const size = random() * 2 + 1;
    return {
      width: size,
      height: size,
      left: `${random() * 100}%`,
      top: `${random() * 100}%`,
      opacity: random() * 0.7 + 0.3,
      animationDelay: `${random() * 3}s`,
      animationDuration: `${random() * 2 + 2}s`,
    };
  });
}

const STARS = generateStars(50);

// Token configuration for orbiting planets
// Backend returns: { symbol, name, logo_uri, glow_color }
interface ApiOrbitingToken {
  symbol: string;
  name: string;
  logo_uri: string;
  glow_color: string;
}

// Internal token with orbital properties added
interface OrbitingToken {
  symbol: string;
  name: string;
  logoUri: string;
  orbitRadius: number; // 0-1, percentage of max radius
  orbitPeriod: number; // seconds for one complete orbit
  startAngle: number; // initial angle in radians
  size: number; // pixel size of token logo
  glowColor: string; // color for the glow effect
}

// Default tokens to show while loading from backend
const DEFAULT_TOKENS: OrbitingToken[] = [
  {
    symbol: 'ATOM',
    name: 'Cosmos Hub',
    logoUri: 'https://raw.githubusercontent.com/cosmos/chain-registry/master/cosmoshub/images/atom.png',
    orbitRadius: 0.35,
    orbitPeriod: 12,
    startAngle: 0,
    size: 30,
    glowColor: '#8b5cf6',
  },
  {
    symbol: 'WETH',
    name: 'Wrapped ETH',
    logoUri: 'https://assets.coingecko.com/coins/images/2518/thumb/weth.png',
    orbitRadius: 0.55,
    orbitPeriod: 20,
    startAngle: Math.PI,
    size: 28,
    glowColor: '#627eea',
  },
];

// Convert API tokens to internal format with orbital properties
function addOrbitalProperties(tokens: ApiOrbitingToken[]): OrbitingToken[] {
  const count = tokens.length;
  return tokens.map((token, index) => {
    // Distribute tokens across orbits from 0.32 to 0.98
    const orbitRadius = 0.36 + (1.4 * index) / Math.max(count - 1, 1);
    // Slower orbits for outer tokens
    const orbitPeriod = 10 + index * 6;
    // Spread start angles evenly plus some offset for visual interest
    const startAngle = (2 * Math.PI * index) / count + (index % 2) * (Math.PI / 8);
    // Vary sizes slightly
    const size = 28 - Math.floor(index / 3) * 2;
    
    return {
      symbol: token.symbol,
      name: token.name,
      logoUri: token.logo_uri,
      orbitRadius,
      orbitPeriod,
      startAngle,
      size: Math.max(size, 20), // Minimum size of 20
      glowColor: token.glow_color,
    };
  });
}

// Gravity Bridge Logo with actual logo image
const GRAVITY_LOGO_URL = 'https://raw.githubusercontent.com/cosmos/chain-registry/master/gravitybridge/images/grav.png';

const GravityLogo = ({ size = 100 }: { size?: number }): JSX.Element => (
  <div className="relative" style={{ width: size, height: size }}>
    {/* Outer glow effect */}
    <div
      className="absolute inset-0 rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.6) 0%, rgba(79, 70, 229, 0.3) 40%, transparent 70%)',
        transform: 'scale(1.5)',
      }}
    />
    {/* Inner glow ring */}
    <div
      className="absolute inset-0 rounded-full"
      style={{
        background: 'radial-gradient(circle, rgba(165, 180, 252, 0.4) 0%, rgba(99, 102, 241, 0.2) 50%, transparent 70%)',
        transform: 'scale(1.2)',
      }}
    />
    {/* Logo container with border */}
    <div
      className="absolute inset-0 rounded-full flex items-center justify-center"
      style={{
        background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 50%, #818cf8 100%)',
        boxShadow: '0 0 30px rgba(99, 102, 241, 0.6), inset 0 0 20px rgba(255, 255, 255, 0.1)',
      }}
    >
      {/* Actual Gravity logo image */}
      <img
        src={GRAVITY_LOGO_URL}
        alt="Gravity Bridge"
        className="rounded-full drop-shadow-lg"
        style={{
          width: size * 0.75,
          height: size * 0.75,
        }}
      />
    </div>
  </div>
);

interface SolarSystemProps {
  className?: string;
}

export function SolarSystem({
  className = '',
}: SolarSystemProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const [time, setTime] = useState(0);
  const [tokens, setTokens] = useState<OrbitingToken[]>(DEFAULT_TOKENS);
  const [containerSize, setContainerSize] = useState({ width: 600, height: 600 });
  const animationRef = useRef<number>();

  // Fetch orbiting tokens from backend
  useEffect(() => {
    const fetchTokens = async (): Promise<void> => {
      try {
        const baseUrl = String(BACKEND_API_URL);
        const response = await fetch(`${baseUrl}/tokens/orbiting?count=6`);
        if (response.ok) {
          const data = (await response.json()) as ApiOrbitingToken[];
          if (data.length > 0) {
            setTokens(addOrbitalProperties(data));
          }
        }
      } catch (error) {
        // Keep using default tokens on error
        console.warn('Failed to fetch orbiting tokens, using defaults:', error);
      }
    };
    
    void fetchTokens();
  }, []);

  // Handle container resize for responsive sizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleResize = (): void => {
      const rect = container.getBoundingClientRect();
      if (rect.width > 0) {
        // Use container width, height matches width for square aspect ratio
        setContainerSize({ width: rect.width, height: rect.width });
      }
    };

    // Initial size calculation
    handleResize();

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    return (): void => {
      resizeObserver.disconnect();
    };
  }, []);

  // Animation loop
  useEffect(() => {
    let startTime: number | null = null;

    const animate = (timestamp: number): void => {
      if (startTime === null) startTime = timestamp;
      const elapsed = (timestamp - startTime) / 1000; // Convert to seconds
      setTime(elapsed);
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return (): void => {
      if (animationRef.current !== undefined) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  const centerX = containerSize.width / 2;
  const centerY = containerSize.height / 2;
  const maxRadius = Math.min(containerSize.width, containerSize.height) / 2 - 40;

  return (
    <div
      ref={containerRef}
      className={`relative w-full ${className}`}
      style={{ minHeight: containerSize.height > 0 ? containerSize.height : 400 }}
    >
      {/* Background with stars */}
      <div className="absolute inset-0 overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900">
        {/* Animated stars */}
        <div className="absolute inset-0">
          {STARS.map((star, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white animate-pulse"
              style={{
                width: star.width,
                height: star.height,
                left: star.left,
                top: star.top,
                opacity: star.opacity,
                animationDelay: star.animationDelay,
                animationDuration: star.animationDuration,
              }}
            />
          ))}
        </div>

        {/* SVG for orbits and tokens */}
        <svg
          width={containerSize.width}
          height={containerSize.height}
          className="absolute inset-0"
          style={{ overflow: 'visible' }}
        >
          <defs>
            {/* Glow filters for each token */}
            {tokens.map((token) => (
              <filter
                key={`glow-${token.symbol}`}
                id={`glow-${token.symbol}`}
                x="-50%"
                y="-50%"
                width="200%"
                height="200%"
              >
                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            ))}

            {/* Orbit path gradient */}
            <linearGradient id="orbitGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.1" />
              <stop offset="50%" stopColor="#818cf8" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#6366f1" stopOpacity="0.1" />
            </linearGradient>
          </defs>

          {/* Orbit paths */}
          {tokens.map((token) => (
            <circle
              key={`orbit-${token.symbol}`}
              cx={centerX}
              cy={centerY}
              r={token.orbitRadius * maxRadius}
              fill="none"
              stroke="url(#orbitGradient)"
              strokeWidth="1"
              strokeDasharray="4 4"
              opacity="0.5"
            />
          ))}

          {/* Orbiting tokens */}
          {tokens.map((token) => {
            const angle =
              token.startAngle + (2 * Math.PI * time) / token.orbitPeriod;
            const x = centerX + Math.cos(angle) * token.orbitRadius * maxRadius;
            const y = centerY + Math.sin(angle) * token.orbitRadius * maxRadius;

            return (
              <g key={token.symbol} filter={`url(#glow-${token.symbol})`}>
                {/* Orbit trail effect */}
                <circle
                  cx={x}
                  cy={y}
                  r={token.size / 2 + 4}
                  fill={token.glowColor}
                  opacity="0.3"
                />
                {/* Token container */}
                <circle
                  cx={x}
                  cy={y}
                  r={token.size / 2 + 2}
                  fill="#1e1b4b"
                  stroke={token.glowColor}
                  strokeWidth="2"
                />
                {/* Token image */}
                <image
                  href={token.logoUri}
                  x={x - token.size / 2}
                  y={y - token.size / 2}
                  width={token.size}
                  height={token.size}
                  clipPath={`circle(${token.size / 2}px at ${token.size / 2}px ${token.size / 2}px)`}
                />
              </g>
            );
          })}
        </svg>

        {/* Central Gravity Logo */}
        <div
          className="absolute transform -translate-x-1/2 -translate-y-1/2"
          style={{
            left: centerX,
            top: centerY,
          }}
        >
          <div className="relative flex items-center justify-center">
            {/* Pulsing rings around center */}
            <div
              className="absolute rounded-full border-2 border-indigo-400 opacity-30 animate-ping"
              style={{
                width: 140,
                height: 140,
                animationDuration: '3s',
              }}
            />
            <div
              className="absolute rounded-full border border-indigo-300 opacity-20 animate-ping"
              style={{
                width: 170,
                height: 170,
                animationDuration: '4s',
              }}
            />
            <div
              className="absolute rounded-full border border-indigo-200 opacity-10 animate-ping"
              style={{
                width: 200,
                height: 200,
                animationDuration: '5s',
              }}
            />
            <GravityLogo size={110} />
          </div>
        </div>

        {/* Token labels (on hover or always visible on larger screens) */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex flex-wrap justify-center gap-2 text-xs text-indigo-200/70">
            {tokens.map((token) => (
              <span
                key={token.symbol}
                className="px-2 py-1 rounded-full bg-indigo-950/50 backdrop-blur-sm border border-indigo-500/20"
              >
                {token.symbol}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SolarSystem;
